import '@module/SearchBox/searchbox.scss';
import { EventDispatcher, add14Days, classList, deepCopy, getUrlVars, hideElement, isDesktopView, isHomePage, isMobileView, isNetworkError, l, lLink, logEvent, qs, qsa, setUrlParameter, showElement, sleep, slideUp, storedItemCreate, storedItemRead, tabTrappingHelper } from '@utils/toolbox';
import { dateAddWeeks, dateDifferenceInDays, dateFormat, dateFormatYMD, dateGetDate, dateGetDay, dateGetMonth, dateIsAfter, dateIsBefore, dateIsSameDay, dateParseISO, dateStartOfDay } from '@utils/dateFns';
import { eventCategories, events } from '@js/utils/event';
import { Base64 } from '@js/plugins/base64';
import { Calendar } from '@components/calendar';
import { CalendarRenderer } from '@js/components/calendarRenderer';
import { ConnectApi } from '@js/plugins/connectApi';
import { Modal } from '@js/components/modal';
import { loadingOverlay } from '@module/LoadingOverlay/LoadingOverlay';
import { SearchProgressBox } from '@components/searchProgressBox';
import { useSearchFieldMulti } from '@/src/vue/components/SearchFieldMulti/SearchFieldMulti';
import { createVueApp } from '@/src/vue/utils/createVueApp';
import Passengers from '@/src/vue/components/Passengers/Passengers.vue';
import { toaster } from '@js/components/toast';
const _defaults = {
	id: 'abSearchBox',
	default: true,
	dates: {
		today: js_params.isoDates.today,
		from: js_params.isoDates.fromIndex,
		to: js_params.isoDates.toIndex
	},
	config: {
		tripType: 1, /* return */
		cabin: 0,
		cabinBaggage: 0,
		legs: [],
		nearbyDates: 0,
		baggage: 0,
		passengers: {
			adults: 1,
			child: {
				count: 0,
				ages: []
			},
			youth: {
				count: 1,
				ages: [null]
			},
			activeView: 'default',
			isYouthTicketEnabled: js_params.module?.SearchBox?.isYouthTicketEnabled || false
		},
		direct: 0,
		climateComp: 0,
		flexTicket: 0
	},
	leg: {
		fromIatas: [],
		fromIata: '',
		from: '',
		toIatas: [],
		toIata: '',
		to: '',
		departDate: js_params.isoDates.fromIndex,
		returnDate: js_params.isoDates.toIndex,
		fromMetro: false,
		toMetro: false
	},
	userSavedSearch: storedItemRead('userSavedSearch'),
	calendarRender: {
		departTitle: `<span>${l('dateselector.title.chooseDepartureDate')}</span>`,
		returnTitle: `<span>${l('dateselector.title.chooseReturnDate')}</span>`
	},
	errors: {
		departNotSet: l('searchBox.error.notSetDeparture'),
		returnNotSet: l('searchBox.error.notSetDestination'),
		dateNotSet: l('searchBox.error.notSetDate'),
		departIsReturn: l('searchBox.error.sameDepartureAndDestination'),
		passengersNotSet: l('searchBox.error.passengersNotSet'),
		childAgesNotSet: l('searchBox.errors.childrenMissingAges'),
		youthAgesNotSet: l('searchBox.errors.youthMissingAges'),
		departDateIsAfterReturn: l('searchBox.error.departDateIsAfterReturn'),
		datesAreInThePast: l('searchBox.error.datesAreInThePast'),
		infantsWithoutSeat: l('searchBox.error.infantsWithoutSeat')
	}
};

export class SearchBox {
	constructor (opts) {
		window.searchBox = this;
		this.id = opts.id;
		this.storeInLocalStorage = opts?.storeInLocalStorage ?? true;
		this.passengerTypeTranslations = opts.passengerTypeTranslations || _defaults.passengerTypeTranslations;
		this.html = document.getElementById(this.id);
		this.calendarRender = null;

		this.datePickers = null;
		this.isUserSavedSearch = (_defaults.userSavedSearch) ? true : false;
		this.showresultMode = opts.showresultMode || false;
		this._callbacks = opts.callbacks || {};
		this.showresultURL = '/';
		this.resultCount = 0;
		this.request = null;
		this.isSearching = false;
		this.sameDayDateModal = null;
		this.searchFields = [];
		this.maxNumberOfLegs = 5;
		this.isYouthTicketEnabled = js_params.module?.SearchBox?.isYouthTicketEnabled || false;
		this.initialScrollPos = 0;
		this.isCarbon = opts.isCarbon;

		// duck tape fix
		this.nullCount = 0;

		// Can be removed in the future - used to migrate old saved searches
		const fixMissingActiveView = (data) => {
			if (!data.passengers.activeView) {
				console.log('Log old schema - migrating');
				data.passengers.activeView = _defaults.config.passengers.activeView;
				data.passengers.isYouthTicketEnabled = this.isYouthTicketEnabled;
				data.passengers.youth = _defaults.config.passengers.youth;
			}
			return data;
		};

		if (opts.config) {
			// Ensure legs array exists and merge with defaults
			if ('legs' in opts.config) {
				for (let i = 0; i < opts.config.legs.length; i++) {
					opts.config.legs[i] = Object.assign({}, _defaults.leg, opts.config.legs[i]);
				}
			} else {
				opts.config.legs = [_defaults.leg];
			}

			const merged = Object.assign({}, _defaults.config, opts.config);

			const fixed = fixMissingActiveView(merged);

			this.initData(fixed);
		} else {
			const userSavedSearch = this.getDataFromLocalStorage();
			if (this.isUserSavedSearch && isHomePage() && userSavedSearch) {
				// Fix missing activeView for backwards compatibility
				const fixed = fixMissingActiveView(userSavedSearch);

				this.initData(fixed);
			} else {
				this.initData(_defaults.config);
				this.data.legs.push(_defaults.leg);
			}
		}

		// Here we check if the dates of the search data are in the past.
		// If they are, we reset them...
		// Might be ok to delete this section as a similar operation is done in the in getDataFromLocalStorage

		if (typeof this.data.legs !== 'undefined') {
			this.data.legs.forEach((leg) => {
				const today = new Date();
				if (!leg.departDate || dateIsBefore(dateStartOfDay(dateParseISO(leg.departDate)), dateStartOfDay(today))) {
					leg.departDate = _defaults.dates.from;

					if (this.data.tripType === 1) {
						leg.returnDate = _defaults.dates.to;
					}
				}
			});
		}


		this.initialData = deepCopy(this.data);

		this.initCalendarRenderer(opts.calendarRender);
		this.initCalendar();

		if (!this.isCarbon) {
			this.setCalendarRendererView();
		}

		this.addLegs();
		this.events();

		if (!this.isCarbon) {
			this.setView();

			if (this.isUserSavedSearch) {
				this.setCheckboxes();
				this.setCabin();
			} else if (opts.config) {
				this.setCabin();
				this.setCheckboxes();
			}
		}

		const urlVars = getUrlVars();

		if (this.showresultMode) {
			const nav = this.html.getElementsByClassName('abSearchBox__nav')[0];

			if (nav) {
				nav.classList.add('abTabNav--transparent');
			}

			if (urlVars.id) {
				this.showresultURL = `/${lLink('global.navigation.resultsLink')}?id=${urlVars.id}`;
			}
		} else {
			if (urlVars.direct && parseInt(urlVars.direct, 10) === 1) {
				this.data.direct = 1;
				this.setDirectCheckbox();
			}

			if (urlVars.climateCompensated && parseInt(urlVars.climateCompensated, 10) === 1) {
				this.data.climateComp = 1;
				this.setClimateCompCheckbox();
			}

			if (urlVars.flexTicket && parseInt(urlVars.flexTicket, 10) === 1) {
				this.data.flexTicket = 1;
				this.setFlexTicketCheckbox();
			}

			if (urlVars.baggage && parseInt(urlVars.baggage, 10) === 1) {
				this.data.baggage = 1;
				this.setBaggageCheckbox();
			}

			if (urlVars.cabinBaggage && parseInt(urlVars.cabinBaggage, 10) === 1) {
				this.data.cabinBaggage = 1;
				this.setCabinBaggageCheckbox();
			}
		}

		this.initPassengers();

		return this;
	}

	initPassengers () {
		this.passengers = createVueApp(Passengers, {
			passengers: this.data.passengers,
			'onUpdate:passengers': (val) => {
				this.data.passengers = val;
			}
		}).mount('.abSearchBox__row--passengers');
	}


	initData (value) {
		this.data = value;
		this.unwatch?.();
		this.data = reactive(value);
		this.unwatch = watch(this.data, () => {
			this.saveSearchToLocalStorage();
			// if multi city re-render the summary
			if (this.data.tripType === 2) {
				this.renderMultiCitySummary();
			}
		}, { deep: true });
	}

	initCalendarRenderer (calendarRender) {
		this.calendarRender = new CalendarRenderer(
			{
				html: calendarRender,
				callbacks:
				{
					onShow: () => {
						if (isMobileView()) {
							this.initialScrollPos = window.scrollY;
							if (this.showresultMode) {
								this.setFixedStyles();
							}
						}
					},
					onHide: () => {
						if (isMobileView()) {
							window.scrollTo({ top: this.initialScrollPos, behavior: 'smooth' });
						}
						if (this.datePickers.tripType === 1) {
							if (this.datePickers.departSelected) {
								this.datePickers.departSelected = false;

								const departDateObject = new Date(this.datePickers.departDate);
								const returnDateObject = new Date(this.datePickers.returnDate);

								const departIsBeforeReturn = dateIsBefore(departDateObject, returnDateObject);
								const datesAreTheSame = dateIsSameDay(departDateObject, returnDateObject);

								if (departIsBeforeReturn || datesAreTheSame) {
									this.setDateField(this.datePickers.departField, this.datePickers.departDate);
									this.setDateField(this.datePickers.returnField, this.datePickers.returnDate);
								}
							}
						}

						this.blurFields();

						if (this.showresultMode) {
							this.removeFixedStyles();
						}
					},
					onReset: () => {
						this.datePickers.resetDates();
					},
					onSlide: (direction) => {
						scrollCalendars(direction);
					}
				}
			});
	}

	initCalendar () {
		this.datePickers = new Calendar(
			{
				searchBox: this,
				callbacks:
				{
					onDepartDatePicked: async (instance) => {
						this.toggleRecentSearchesOverlay(false);
						const { departField, departDate, returnField, returnDate } = instance;
						if (isMobileView() && instance.tripType === 1) {
							this.calendarRender.setTitle(_defaults.calendarRender.returnTitle);
						}

						const departDateObject = new Date(departDate);
						const returnDateObject = new Date(returnDate);
						const departIsAfterReturn = dateIsAfter(departDateObject, returnDateObject);
						const datesAreTheSame = dateIsSameDay(departDateObject, returnDateObject);

						// Check for returnField since isAfter will return true when returnDateObject is null
						if (returnField && (departIsAfterReturn || datesAreTheSame)) {
							this.setDateField(returnField, departDate);
						}

						this.setDateField(departField, departDate);
						logEvent(events.editsearchDateDeparture, eventCategories.search, departDate);

						if (instance.tripType !== 1) {
							await sleep(400);
							this.calendarRender.hide();
							this.exitTrapping?.();
							await sleep(1000);
							this.toggleRecentSearchesOverlay(true);
						} else {
							this.blurFields();
							this.focusField(returnField, 'abSearchBox__row--focus-return');
						}
					},
					onReturnDatePicked: async (instance) => {
						this.toggleRecentSearchesOverlay(false);
						const { departField, departDate, returnField, returnDate } = instance;
						const departDateObject = new Date(departDate);
						const returnDateObject = new Date(returnDate);
						const departIsBeforeReturn = dateIsBefore(departDateObject, returnDateObject);
						const datesAreTheSame = dateIsSameDay(departDateObject, returnDateObject);

						if (departIsBeforeReturn || datesAreTheSame) {
							this.setDateField(departField, departDate);
						}

						this.setDateField(returnField, returnDate);
						logEvent(events.editsearchDateDeparture, eventCategories.search, returnDate);

						if (instance.tripType === 1) {
							await sleep(400);
							this.calendarRender.hide();
						}
						await sleep(1000);
						this.toggleRecentSearchesOverlay(true);

						this.exitTrapping?.();
					},
					onScroll: () => {
						scrollCalendars();
					}
				}
			});
	}

	// #recent-searches .searches.style="pointer-events: none;
	toggleRecentSearchesOverlay (isClickable) {
		const element = qs('#recent-searches .searches');
		if (!element) {
			return;
		}

		if (isClickable) {
			element.style.pointerEvents = '';
		} else {
			element.style.pointerEvents = 'none';
		}
	}

	getDataFromLocalStorage () {
		// Check if the departure date is in the past, if so, set departure date a week from today.
		// If return trip, set the return date 14 days from today.
		// If Multi City, repeat date changes, but for each leg.

		const userSavedSearch = storedItemRead('userSavedSearch');
		const tripType = userSavedSearch?.tripType;
		const departDate = userSavedSearch?.legs?.[0]?.departDate;
		if (!userSavedSearch || !tripType || !departDate) {
			return false;
		}
		const departDateObject = dateParseISO(departDate);
		const now = dateStartOfDay(new Date());

		if (dateIsBefore(departDateObject, now)) {
			for (const leg of userSavedSearch.legs) {
				leg.departDate = _defaults.dates.from;

				if (tripType === 1) {
					leg.returnDate = _defaults.dates.to;
				}
			}
		}

		// Override isYouthTicketEnabled from js params so the server is the source of truth for this value
		userSavedSearch.passengers.isYouthTicketEnabled = this.isYouthTicketEnabled;

		// this.initData(Object.assign(_defaults.config, userSavedSearch));
		return userSavedSearch;
	}

	addLegs () {
		const legs = this.html.getElementsByClassName('abSearchBox__leg');
		const leg = legs[legs.length - 1];
		const dataTripType = this.data.tripType;
		const dataLegs = this.data.legs;

		if (dataLegs.length) {
			// we have pre-defined search data - either from cookie or config...

			let field = leg.getElementsByClassName('abSearchBox__depart')[0];
			let date = dataLegs[0].departDate || _defaults.dates.from;

			this.setupDateField({ field, date });

			if (dataTripType === 1) {
				field = leg.getElementsByClassName('abSearchBox__return')[0];
				date = dataLegs[0].returnDate || _defaults.dates.to;

				this.setupDateField({ field, date });
			} else if (dataTripType === 2) {
				const numOfLegs = this.data.legs.length - 1;

				for (let i = 1; i <= numOfLegs; i++) {
					this.renderMultiCityLeg(i);
				}
			}
		} else {
			let field = leg.getElementsByClassName('abSearchBox__depart')[0];
			let date = _defaults.dates.from;

			this.setupDateField({ field, date });

			if (dataTripType === 1) {
				field = leg.getElementsByClassName('abSearchBox__return')[0];
				date = _defaults.dates.to;

				this.setupDateField({ field, date });
			}
		}

		// Add iata Searchfields
		const iataFields = this.html.getElementsByClassName('iata_field_module');
		for (let i = 0; i < iataFields.length; i++) {
			this.addSearchField(iataFields[i], Math.floor(i / 2), i % 2 === 0 ? 'from' : 'to');
		}
	}

	focusCalendar (elem) {
		this.blurFields();

		if (window.innerWidth >= 992) {
			this.calendarRender.blurField();
		}

		this.datePickers.focus = 'depart';
		if (isMobileView()) {
			this.calendarRender.setTitle(_defaults.calendarRender.departTitle);
		}

		this.refreshCalendar(elem);

		this.focusField(elem, 'abSearchBox__row--focus-depart');
	}

	focusField (field, className) {
		const row = field.parentNode.closest('.abSearchBox__row');

		if (row.classList.contains(className)) {
			return false;
		}

		this.html.classList.add('abSearchBox--show-overlay');

		if (document.body.classList.contains('tabmode')) {
			if (field.dataset?.date) {
				this.exitTrapping = tabTrappingHelper(qs('.calendarsContainer'), className, () => this.blurFields(), false, qsa('.Calendar__day:not(.Calendar__day--disabled)'));
			} else {
				this.exitTrapping = tabTrappingHelper(field, className, () => this.blurFields(), className);
			}
		}

		row.classList.add(className);

		if (isMobileView()) {
			if (this.showresultMode) {
				const overlay = this.html.getElementsByClassName('abSearchBox__overlay')[0];
				const searchBoxHeight = document.getElementsByClassName('abShowResultSearchBox__inner')[0].clientHeight;

				overlay.style.height = Math.max(window.innerHeight, searchBoxHeight) + 'px';
			}

			document.body.style.overflow = 'hidden';

			window?.recentSearches?.stop?.();
		}

		return true;
	}

	blurFields () {
		const rows = this.html.getElementsByClassName('abSearchBox__row');
		const focusStyles = ['from', 'to', 'depart', 'return', 'passengers', 'cabin', 'view'];

		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];

			for (let j = 0; j < focusStyles.length; j++) {
				row.classList.remove('abSearchBox__row--focus-' + focusStyles[j]);
			}
		}

		this.html.classList.remove('abSearchBox--show-overlay');

		if (isMobileView()) {
			const overlay = this.html.getElementsByClassName('abSearchBox__overlay')[0];

			if (this.showresultMode) {
				overlay.style.height = '';
			}

			document.body.style.overflow = '';

			setTimeout(() => {
				window.scrollTo({ top: this.initialScrollPos, behavior: 'smooth' });
			}, 60);
		}
	}

	renderMultiCitySummary () {
		const multiSummary = this.html.querySelector('.abSearchBox__multi-summary');
		if (!multiSummary) {
			return;
		}
		const legsNb = this.data.legs.length;

		const isAllNumeric = str => str.match(/^[0-9]+$/);
		const truncateString = (str, num) => str.length > num ? str.slice(0, num) + '...' : str;

		let html = '';
		const legWidth = `calc(${100 / legsNb}% - 1rem)`;

		this.data.legs.forEach((leg, index) => {
			const getLocation = (iatas, legsNb) => {
				const [iataObj] = iatas || [];
				// Full
				if (legsNb === 2 && isDesktopView() && iataObj?.text) {
					return iataObj.text;
				}
				// Fallback for fake metro
				if (iataObj?.iata && isAllNumeric(iataObj.iata)) {
					return truncateString(iataObj.text, 5);
				}
				// Iata
				return iataObj?.iata || '';
			};

			const departure = getLocation(leg?.fromIatas);
			const arrival = getLocation(leg?.toIatas, legsNb);

			const legDepartDateObject = new Date(leg.departDate);
			const dateExtra = (legsNb <= 3) ? `, <small>${dateFormat(legDepartDateObject, 'eee')}</small>` : '';

			html += `
    <div class="abSearchBox__multi-summary--leg" style="width: ${legWidth}">
      <span class="abSearchBox__label">Flight ${index + 1}</span>
      <span class="abSearchBox__value">
        ${departure || ''} 
        <span class="icon icon-arrow-right"></span> 
        ${arrival || ''} 
        <span class="abSearchBox__multi-summary--date">
          ${dateFormat(legDepartDateObject, 'MMM d')}${dateExtra}
        </span>
      </span>
    </div>
  `;
		});

		multiSummary.innerHTML = html;
	}

	renderMultiCityLeg (legIndex = null) {
		const legs = this.html.getElementsByClassName('abSearchBox__leg');
		const previousLeg = legs[legs.length - 1];
		const newLeg = previousLeg.cloneNode(true);

		const newLegIndex = legIndex || parseInt(previousLeg.dataset.legIndex, 10) + 1;
		const legData = this.data.legs[newLegIndex];

		newLeg.dataset.legIndex = newLegIndex;
		newLeg.getElementsByClassName('abSearchBox__circle--number')[0].textContent = newLegIndex + 1;

		let newLegDate = legData?.departDate || _defaults.dates.from;
		// Check if new leg date is before previous leg date
		const previousLegDate = qs('.abSearchBox__depart', previousLeg)?.dataset?.date || _defaults.dates.from;
		// Add 1 week to the previous leg date
		if (dateIsBefore(dateParseISO(newLegDate), dateParseISO(previousLegDate))) {
			newLegDate = dateFormat(dateAddWeeks(dateParseISO(previousLegDate), 1), 'yyyy-MM-dd');
		}

		this.setupDateField({ field: newLeg.getElementsByClassName('abSearchBox__depart')[0], date: newLegDate });

		const newLegIataFields = newLeg.getElementsByClassName('iata_field_module');

		for (let i = 0; i < newLegIataFields.length; i++) {
			this.addSearchField(newLegIataFields[i], newLegIndex, i % 2 === 0 ? 'from' : 'to');
		}

		const previousLegInput = previousLeg.querySelectorAll('input.SearchFieldMulti__field')[1];

		this.html.insertBefore(newLeg, previousLeg.nextElementSibling);

		qs('#abSearchbox__dates', newLeg)?.remove?.();

		if (this.isCarbon) {
			qsa('.emission-aircraft-input', newLeg).forEach((inputElement) => {
				inputElement.value = '';
				inputElement.removeAttribute('data-value');
			});
		}

		// Enabling this will cause issues on index
		// Repro: visit index, change to multi city and refresh
		// Buyer beware!
		// this.updateLegs();
	}

	renderMultiCityLegNumbers () {
		const legs = this.html.getElementsByClassName('abSearchBox__leg');

		for (let i = 0; i < legs.length; i++) {
			const leg = legs[i];
			const circle = leg.getElementsByClassName('abSearchBox__circle--number')[0];

			leg.dataset.legIndex = i;
			circle.textContent = i + 1;
		}
	}

	renderMultiCityRemoveBtns () {
		const legs = this.html.getElementsByClassName('abSearchBox__leg');

		if (legs.length > 2 || (this.isCarbon && legs.length > 1)) {
			qsa('.abSearchBox__remove-leg').forEach((btn) => {
				btn.classList.add('abSearchBox__remove-leg--visible');
			});
		} else {
			qsa('.abSearchBox__remove-leg').forEach((btn) => {
				btn.classList.remove('abSearchBox__remove-leg--visible');
			});
		}
	}

	addSearchField (field, leg = 0, bound) {
		// Check if bound is already set
		if (this.searchFields?.[leg]?.[bound]) {
			return;
		}

		const label = bound === 'from' ? l('global.simplePhrases.From') : l('global.simplePhrases.To');

		const parent = field.closest('.SearchFieldMulti');

		if (!parent) {
			return;
		}


		this.searchFields[leg] = this.searchFields[leg] || {};

		const props = reactive({
			label: label,
			legIndex: leg,
			bound: bound,
			data: this.data
		});

		this.searchFields[leg][bound] = {
			component: useSearchFieldMulti(props),
			props: props
		};

		this.searchFields[leg][bound].component.mount(parent);
	}


	setView () {
		const nav = this.html.getElementsByClassName('abSearchBox__nav')[0];
		const btns = nav.children;

		for (let i = 0; i < btns.length; i++) {
			btns[i].classList.remove('active');
		}

		if (this.data.tripType === 0) {
			// oneway

			this.html.classList.remove('abSearchBox--multi');
			this.html.classList.remove('abSearchBox--return');
			this.html.classList.add('abSearchBox--oneway');

			btns[0].classList.add('active');
		} else if (this.data.tripType === 2) {
			// multi

			const legs = this.html.getElementsByClassName('abSearchBox__leg');

			this.html.classList.remove('abSearchBox--oneway');
			this.html.classList.remove('abSearchBox--return');
			this.html.classList.add('abSearchBox--multi');

			btns[2].classList.add('active');

			if (legs.length === 1 && !this.isCarbon) {
				this.renderMultiCityLeg();
			}

			this.renderMultiCityRemoveBtns();

			if (this.showresultMode) {
				this.renderMultiCitySummary();
			}
		} else {
			// return

			this.html.classList.remove('abSearchBox--multi');
			this.html.classList.remove('abSearchBox--oneway');
			this.html.classList.add('abSearchBox--return');

			btns[1].classList.add('active');
		}

		this.setViewField();
		this.setCalendarRendererView();
	}


	setViewField () {
		const viewField = this.html.querySelector('.abSearchBox__view');
		const fieldValue = viewField.querySelector('.abSearchBox__value');
		const buttons = viewField.querySelectorAll('[data-action="set-view"]');

		buttons.forEach((button) => {
			if (parseInt(button.dataset.type, 10) === this.data.tripType) {
				button.classList.add('abBtn--ticked');

				fieldValue.innerHTML = button.querySelector('span').innerText;
			} else {
				button.classList.remove('abBtn--ticked');
			}
		});
	}

	setCabin () {
		const buttons = this.html.querySelectorAll('button[data-action="set-cabin"]');

		buttons.forEach((button) => {
			if (parseInt(button.value, 10) === this.data.cabin) {
				button.click();
			}
		});
	}

	setDirectCheckbox () {
		const checkboxes = this.html.querySelectorAll('input.checkbox-direct');
		checkboxes.forEach(checkbox => checkbox.checked = this.data.direct);
		// checkboxes[0].dispatchEvent(new Event('change'));
	}

	setBaggageCheckbox () {
		const checkbox = this.html.getElementsByClassName('abSearchBox__baggage')[0];
		checkbox.checked = this.data.baggage;
	}

	setCabinBaggageCheckbox () {
		const checkbox = this.html.getElementsByClassName('abSearchBox__cabinBaggage')[0];
		checkbox.checked = this.data.cabinBaggage;
	}

	setClimateCompCheckbox () {
		const checkbox = this.html.getElementsByClassName('abSearchBox__climateComp')[0];
		checkbox.checked = this.data.climateComp;
	}

	setFlexTicketCheckbox () {
		const checkbox = this.html.getElementsByClassName('abSearchBox__flexTicket')[0];
		checkbox.checked = this.data.flexTicket;
	}

	setNearbyCheckbox () {
		const checkbox = this.html.getElementsByClassName('abSearchBox__nearby')[0];

		if (checkbox) {
			checkbox.checked = this.data?.nearbyDates;
		}
	}

	setDateField (field, strDate, updateModel = true) {
		const date = dateParseISO(strDate);

		field.dataset.date = dateFormatYMD(date);
		field.getElementsByClassName('abSearchBox__value')[0].innerHTML = `${dateFormat(date, 'MMM d')}, <small>${dateFormat(date, 'EEE')}</small>`;

		// Get leg index
		const legIndex = parseInt(field.closest('.abSearchBox__leg').dataset.legIndex, 10) || 0;
		const isDepart = field.classList.contains('abSearchBox__depart');

		if (updateModel) {
			if (isDepart) {
				this.data.legs[legIndex].departDate = dateFormatYMD(date);
			} else {
				this.data.legs[legIndex].returnDate = dateFormatYMD(date);
			}
		}

		if (this.data.tripType === 1) {
			this.setDurationLabel();
		}

		return this;
	}

	setDurationLabel () {
		const durationLabel = this.html.getElementsByClassName('abSearchBox__duration')[0];

		const days = dateDifferenceInDays(new Date(this.data.legs[0].returnDate), new Date(this.data.legs[0].departDate));

		const dayTranslation = days > 1 ? l('global.calendar.days') : l('global.calendar.day');

		if (days === 0) {
			hideElement(durationLabel);
		} else if (durationLabel.style.display === 'none') {
			showElement(durationLabel);
		}
		durationLabel.children[1].innerHTML = `${days} ${dayTranslation}`;
	}

	scrollTopView (elem, offset) {
		this.initialScrollPos = window.scrollY;
		const elemOffset = elem.getBoundingClientRect().top + window.scrollY - offset;

		window.scrollTo({ top: elemOffset });
	}

	updateLegs (syncData = false) {
		const legs = this.html.getElementsByClassName('abSearchBox__leg');

		// back up legs
		const legsBackup = deepCopy(this.data.legs);

		const newLegs = [];

		for (let i = 0; i < legs.length; i++) {
			const leg = legs[i];

			const departDateEl = qs('.abSearchBox__depart', leg);
			const returnDateEl = qs('.abSearchBox__return', leg);

			// Create new leg for pushing into newLegs array later
			const newLeg = {
				fromIatas: legsBackup?.[i]?.fromIatas || [],
				toIatas: legsBackup?.[i]?.toIatas || [],
				departDate: departDateEl.dataset.date,
				returnDate: returnDateEl?.dataset?.date
			};


			// one-way
			if (this.data.tripType === 0) {
				newLeg.returnDate = null;
			} else if (this.data.tripType === 1) {
				// If data has exactly two legs then move over the dates
				if (legsBackup?.length >= 2) {
					newLeg.departDate = legsBackup[0].departDate;
					newLeg.returnDate = legsBackup[1].departDate;
				}
			// multi-city
			} else if (this.data.tripType === 2) {
				// Legs can only have one fromIata and one toIata in multi-city mode
				newLeg.fromIatas = newLeg.fromIatas.slice(0, 1);
				newLeg.toIatas = newLeg.toIatas.slice(0, 1);
			}

			newLegs.push(newLeg);

			this.setDateField(departDateEl, newLeg.departDate, false);

			if (returnDateEl && newLeg.returnDate) {
				this.setDateField(returnDateEl, newLeg.returnDate, false);
			}

			if (this.data.tripType !== 2) {
				break;
			}

			// If index = 1 then invert the to and from iatas of the previous leg
			if (syncData && i === 1 && legsBackup?.[0]) {
				const prevLeg = legsBackup[0];
				newLegs[1].toIatas = deepCopy(prevLeg.fromIatas);
				newLegs[1].fromIatas = deepCopy(prevLeg.toIatas);
				// Slice to ensure only one iata is set
				newLegs[1].toIatas = newLegs[1].toIatas.slice(0, 1);
				newLegs[1].fromIatas = newLegs[1].fromIatas.slice(0, 1);


				// Add one week to previous leg return date if new leg depart date is before previous leg return date
				newLegs[1].departDate = prevLeg.returnDate || dateFormatYMD(dateAddWeeks(dateParseISO(prevLeg.departDate), 1));
				this.setDateField(departDateEl, newLegs[1].departDate, false);
			}

			// Multy cities don't support return dates
			newLegs[i].returnDate = null;

			// Ensure depature date is not before previous leg return date, if it is adjust it by one week
			if (i > 0 && legsBackup?.[i - 1]) {
				const prevLeg = legsBackup[i - 1];
				if (dateIsBefore(dateParseISO(newLegs[i].departDate), dateParseISO(prevLeg.departDate))) {
					newLegs[i].departDate = dateFormatYMD(dateAddWeeks(dateParseISO(prevLeg.departDate), 1));
					this.setDateField(departDateEl, newLegs[i].departDate, false);
				}
			}
		}


		this.data.legs = newLegs;

		if (this.showresultMode && this.data.tripType === 2) {
			this.renderMultiCitySummary();
		}

		return this;
	}


	setupDateField (params) {
		if (!this.isCarbon) {
			const { field, date } = params;
			const fieldVal = field.getElementsByClassName('abSearchBox__value')[0];

			if (date) {
				const dateObject = dateParseISO(date);
				// get month number and day number
				const monthNumber = dateGetMonth(dateObject);
				const dayNumber = dateGetDay(dateObject);
				const monthName = js_params.months[monthNumber];
				const dayName = js_params.weekdaysSundayFirst[dayNumber];
				const monthDate = dateGetDate(dateObject);

				field.dataset.date = dateFormatYMD(dateObject);
				field.ariaLabel = l('ariaLabels.changeDepartureDate') + ' ' + dateFormat(dateObject, 'd MMMM yyyy');
				fieldVal.innerHTML = `${monthName} ${monthDate}, <small>${dayName}</small>`;
			} else {
				field.dataset.date = '';
				fieldVal.innerHTML = '&nbsp;';
			}

			if (this.data.tripType === 1) {
				this.setDurationLabel();
			}

			return this;
		}
	}

	validateData () {
		let success = true;
		let errorOrigin = 'legs';
		let message = 'none';

		if (this.isYouthTicketEnabled && this.data.passengers.activeView === 'youth') {
			// check if adult passenger number is below 1
			if (this.data.passengers.youth.count <= 0) {
				success = false;
				errorOrigin = 'passengers';
				message = _defaults.errors.passengersNotSet;
			}

			// if not all child ages have been selected...
			if (this.data.passengers.youth.ages.includes(null)) {
				success = false;
				errorOrigin = 'passengers';
				message = _defaults.errors.youthAgesNotSet;
			}
		} else {
			// check if adult passenger number is below 1
			if (this.data.passengers.adults <= 0) {
				success = false;
				errorOrigin = 'passengers';
				message = _defaults.errors.passengersNotSet;
			}

			// if not all child ages have been selected...
			if (this.data.passengers.child.ages.includes(null)) {
				success = false;
				errorOrigin = 'passengers';
				message = _defaults.errors.childAgesNotSet;
			}

			// For every infant there must be an adult or a child passenger above 12 years old
			const numberOfInfants = this.data.passengers.child.ages.filter(age => age === 0).length;
			const numberOfAdults = this.data.passengers.adults;
			const numberOfChildrenAbove12 = this.data.passengers.child.ages.filter(age => age > 11).length;

			if (numberOfInfants > numberOfAdults + numberOfChildrenAbove12) {
				success = false;
				errorOrigin = 'passengers';
				message = _defaults.errors.infantsWithoutSeat;
			}
		}

		// check if there are any legs to begin with
		if (!this.data.legs.length) {
			success = false;
			message = _defaults.errors.departNotSet;
		}

		// loop over legs and check if any destinations are empty...
		this.data.legs.forEach((leg) => {
			const emptyFrom = !leg.fromIatas.length;
			const emptyTo = !leg.toIatas.length;
			let departIsReturn = false;

			for (const a of leg.fromIatas) {
				for (const b of leg.toIatas) {
					if (a.iata === b.iata && a.isMetro === b.isMetro) {
						departIsReturn = true;
					}
				}
			}

			if (emptyFrom) {
				success = false;
				message = _defaults.errors.departNotSet;
			} else if (emptyTo) {
				success = false;
				message = _defaults.errors.returnNotSet;
			} else if (departIsReturn) {
				success = false;
				message = _defaults.errors.departIsReturn;
			}
		});
		// some options are always disabled for multi-city searches -
		// but we want to be able to keep those boxes selected if just switching between single/multi modes
		if (this.data.tripType === 2) /* multi */ {
			this.data.nearbyDates = 0;
			this.data.direct = 0;

			if (!this.validateMultiCityDates()) {
				success = false;
				message = _defaults.errors.departDateIsAfterReturn;
			}
		}

		return { success, errorOrigin, message };
	}

	saveSearchToLocalStorage () {
		if (!this.storeInLocalStorage) {
			return;
		}
		if (!this.data || this.data === {}) {
			console.log('Tried to save empty user search');
			return;
		}
		storedItemCreate('userSavedSearch', this.data);
	}

	formatDataForMultiSearch () {
		const toFormattedIata = ci => ({ iata: ci.iata, isMetro: ci.isMetro });
		const trips = this.data.legs.map(leg => ({
			from: leg.fromIatas.map(toFormattedIata),
			to: leg.toIatas.map(toFormattedIata),
			date: leg.departDate
		}));

		if (this.data.tripType === 1) /* return */ {
			const firstLeg = this.data.legs[0];
			trips.push({
				// We need to flip from and to
				from: firstLeg.toIatas.map(toFormattedIata),
				to: firstLeg.fromIatas.map(toFormattedIata),
				date: firstLeg.returnDate
			});
		}

		return trips;
	}

	getSearchDataForApi () {
		this.updateLegs();

		const { activeView = 'default', adults, child, youth } = this.data.passengers;

		let passengers;
		const formattedTrips = this.formatDataForMultiSearch();


		if (activeView === 'default') {
			passengers = {
				adults,
				child: {
					count: child.count,
					ages: [...child.ages]
				}
			};
		} else if (activeView === 'youth') {
			passengers = {
				youth: {
					count: youth.count,
					ages: [...youth.ages]
				}
			};
		}

		const postData = {
			trips: formattedTrips,
			returntrip: this.data.tripType,
			classType: this.data.cabin,
			passengers: passengers
		};

		return postData;
	}

	submit (skipSameDayConfirmation = false) {
		// if (this.isSearching) {
		// 	return;
		// }

		this.updateLegs();

		const isValidData = this.validateData();

		if (isValidData.success === true) {
			const firstLeg = this.data.legs[0];
			if (!skipSameDayConfirmation && this.data.tripType === 1 && dateIsSameDay(new Date(firstLeg.departDate), new Date(firstLeg.returnDate))) {
				this.showSameDayDateModal();
				return;
			}

			loadingOverlay.show();
			const formattedTrips = this.formatDataForMultiSearch();

			this.isSearching = true;
			this.resultCount = 0;

			const { activeView = 'default', adults, child, youth } = this.data.passengers;

			let passengers;

			if (activeView === 'default') {
				passengers = {
					adults,
					child: {
						count: child.count,
						ages: [...child.ages]
					}
				};
			} else if (activeView === 'youth') {
				passengers = {
					youth: {
						count: youth.count,
						ages: [...youth.ages]
					}
				};
			}

			const postData = {
				trips: formattedTrips,
				returntrip: this.data.tripType,
				classType: this.data.cabin,
				passengers: passengers
			};

			this.initiateSearch([postData]);
		} else {
			// data not valid! Show friendly error...
			loadingOverlay.hide();
			this.isSearching = false;

			// window.alert(isValidData.message);
			if (isValidData.errorOrigin === 'passengers') {
				this.focusPassengersField();
			}
			toaster.newToast(isValidData.message);
		}
	}

	showSameDayDateModal () {
		if (this.sameDayDateModal?.isOpen) {
			return;
		}

		const continueButtonId = 'continueSameDaySearch';
		const cancelButtonId = 'cancelSameDaySearch';
		this.sameDayDateModal = new Modal({
			id: 'sameDayDateModal',
			className: 'modal--searchError',
			hideFooter: false,
			header: `<div class="title">${l('global.simplePhrases.oops')}</div>`,
			body: `<div class="abDisplayMessage abDisplayMessage--column abDisplayMessage--withImageBg" aria-label="${l('searchBox.errors.sameLeaveReturnDate')}" style="display: flex;">
	<img src="/static/v${js_params.version}/assets/images/global/noResultListsPlane.svg" class="abDisplayMessage--withImageBg-bgSvg"/>
	<div class="abDisplayMessage__msg"><span role="alert">${l('searchBox.errors.sameLeaveReturnDate')}</span></div>
</div>`,
			footer: `
				<button id="${cancelButtonId}" class="abBtn abBtn--whiteBlue abBtn--flex">${l('global.simplePhrases.abort')}</button>
				<button id="${continueButtonId}" class="abBtn abBtn--blueWhite abBtn--flex">${l('global.simplePhrases.continueAnyway')}</button>
			`,
			onOpen: () => {
				this.sameDayDateModal.html.addEventListener('click', (event) => {
					if (event.target.id === cancelButtonId) {
						this.sameDayDateModal.close();
					} else if (event.target.id === continueButtonId) {
						this.sameDayDateModal.close();
						this.startSearchAfterDateConfirmation();
					}
				});
			}
		});
		this.sameDayDateModal.open();
	}

	startSearchAfterDateConfirmation () {
		this.sameDayDateModal = null;
		this.submit(true);
	}

	initiateSearch (postData) {
		if (postData[0]) {
			window.aboutToSearch = 1;
			logEvent(events.search, eventCategories.search, JSON.stringify(postData[0]));
		}

		if ('force_server' in js_params.urlVars) {
			postData[0].server = js_params.urlVars.force_server;
		}

		this.request = ConnectApi('Search', 'initiateSearch', postData).then((resp) => {
			const response = resp.results;
			const errors = resp.error;
			const { nearbyDates, direct, baggage, cabinBaggage, climateComp, flexTicket, tripType } = this.data;

			if (errors) {
				document.body.classList.remove('search-loading');
				this.html.classList.remove('abSearchBox--loading');

				if (this.showresultMode) {
					document.querySelector('.abShowResultSearchBox__inner').classList.remove('full-view');
				}

				errors.forEach(error => window.alert(error));

				this.cancelSearch();

				return;
			}

			if (response.id) {
				const { masterid, server } = response;
				const filterParams = {};
				let targetUrl = `/${lLink('global.navigation.resultsLink')}?id=${response.id}`;

				if (direct === 1) {
					filterParams.numStops = 0;
				}

				const numberOfBags = this.getNumberOfBags();

				if (baggage === 1) {
					filterParams.baggageIncluded = numberOfBags;
				}

				if (cabinBaggage === 1) {
					filterParams.cabinBaggageIncluded = numberOfBags;
				}

				if (climateComp === 1) {
					filterParams.climateCompensated = 1;
				}

				if (flexTicket === 1) {
					filterParams.flexTicket = 1;
				}

				if (Object.keys(filterParams).length) {
					const params = Base64.encode(JSON.stringify(filterParams));

					targetUrl += `&filter=${params}`;
				}

				if (this.data.nearbyDates) {
					targetUrl += `&nearbyDates=${this.data.nearbyDates}`;
				}

				this.saveSearchToLocalStorage();
				this.showresultURL = targetUrl;

				setTimeout(() => {
					this.continueSearch(
						{
							responseId: response.id
						});
				}, 3500);
			}
		}).catch((error) => {
			loadingOverlay.hide();
			this.isSearching = false;

			if (isNetworkError(error)) {
				error = { error: l('error.e14') };
			} else if (this.showresultMode && (parseInt(_defaults.userSavedSearch?.nearbyDates) === 1)) {
				error = { error: l('error.e11') };
			} else {
				error = { error: l('error.e9') };
			}

			this.cancelSearch();
			this.handleErrorMessage(error);
		});
	}

	continueSearch (requestInformation) {
		return new Promise(async (resolve, reject) => {
			const handleError = (error = '') => {
				if (isNetworkError(error)) {
					error = { error: l('error.e14') };
				} else if (error === '') {
					error = { error: l('error.e9') };
				} else if (this.showresultMode && (parseInt(_defaults.userSavedSearch?.nearbyDates) === 1)) {
					error = { error: l('error.e11') };
				}
				this.cancelSearch();
				loadingOverlay.hide();

				if (this._callbacks.onSearchError) {
					this._callbacks.onSearchError(this);
				}

				if (this.searchProgressBox) {
					this.searchProgressBox.close();
				}

				this.handleErrorMessage(error);
			};

			this.isSearching = true;

			if (typeof this.searchProgressBox === 'undefined') {
				if (this.showresultMode) {
					this.searchProgressBox = new SearchProgressBox();
				}
			}

			this.request = ConnectApi('Search', 'update', [requestInformation], true).then(async (resp) => {
				// duck tape fix
				if (resp === null) {
					if (this.nullCount < 1) {
						this.nullCount += 1;
						await sleep(1000);
						await this.continueSearch(requestInformation);
					} else {
						this.nullCount = 0;
						handleError();
						return false;
					}
				} else if (resp.error) {
					if (this.data.nearbyDates) {
						this.data.forceNearbyDates = true;
						this.showresultURL = setUrlParameter(this.showresultURL, 'forceNearbyDates', 1);

						await this._callbacks.onSearchComplete(this);
						this.isSearching = false;

						if (this.searchProgressBox) {
							this.searchProgressBox.close();
						}
					} else {
						handleError(resp);
					}
					return false;
				} else if (resp.results.status === 'searching') {
					if (resp.previewgroup) {
						if (this._callbacks.onPreviewReceived) {
							this._callbacks.onPreviewReceived(this, resp.previewgroup);
						}

						if (this.showresultMode && this.searchProgressBox) {
							this.searchProgressBox.setNumberOfFoundResults(resp.results.totalRows);
						}

						this.resultCount = resp.results.totalRows;
					}

					if (this.isSearching) {
						await sleep(1000);
						await this.continueSearch(requestInformation);
					}
				} else if (resp.results.status === 'complete') {
					if (this._callbacks.onSearchComplete) {
						await this._callbacks.onSearchComplete(this);
					}

					this.isSearching = false;

					if (this.searchProgressBox) {
						this.searchProgressBox.close();
					}
				}
			}).catch(handleError);
			await this.request;
			resolve();
		});
	}

	getNumberOfBags () {
		// If youth search count the number of youth
		if (this.data.passengers.activeView === 'youth') {
			return this.data.passengers.youth.length;
		}
		const numberOfChildrenOver1 = this.data.passengers.child.ages.filter(age => age > 1).length;
		return this.data.passengers.adults + numberOfChildrenOver1;
	}

	cancelSearch () {
		this.isSearching = false;

		this?.request?.abort?.();
	}

	showErrorModal (error) {
		let errorMessage = error.error_code === 'E8' ? l('searchbox.error.noResultsWithSelectedDates') : error?.error || l('error.e9');
		const states = [
			{
				buttons: [
					{
						id: 'closeModal',
						html: `<button id="closeModal" class="abBtn abBtn--whiteBlue abBtn--flex"><span>${l('searchbox.error.noResultsWithSelectedDates.cancel')}</span></button>`,
						callback: () => {
							this.errorModal.close();
						}
					},
					{
						id: 'forceNearbyDates',
						html: `<button id="forceNearbyDates" class="abBtn--blueWhite abBtn abBtn--flex"><span>${l('searchbox.error.noResultsWithSelectedDates.confirm')}</span></button>`,
						callback: () => {
							this.showresultURL = setUrlParameter(this.showresultURL, 'forceNearbyDates', 1);
							window.location = this.showresultURL;
						}
					}
				]
			},
			{
				buttons: [
					{
						id: 'closeModal',
						html: `<button id="closeModal" class="abBtn--blueWhite abBtn abBtn--flex">${l('searchBox.search.searchBtnText') }</button>`,
						callback: () => {
							this.errorModal.close();
						}
					}
				]
			}

		];
		let ctaButton = error?.error_code === 'E8' ? states[0] : states[1];
		// If one-way or return search and E8 error, show both buttons
		if (this.data.tripType === 2 && error?.error_code === 'E8' && this.data.legs.length <= 2) {
			ctaButton = states[0];
			errorMessage = error?.error ?? l('searchbox.error.noResultsWithSelectedDates');
		// If multi-city search and E8 error, but more than 2 legs, only show close button
		} else if (this.data.tripType === 2 && error?.error_code === 'E8') {
			errorMessage = error?.error ?? l('error.e8');
			ctaButton = states[1];
		}
		this.errorModal = new Modal({
			id: 'searchErrorModal',
			className: 'modal--searchError',
			hideFooter: false,
			hideOnSwipe: true,
			resetScroll: true,
			header: `<div class="title">${l('global.simplePhrases.oops')}</div><span class="icon-circle-cross"></span>`,
			footer: ctaButton.buttons.map(button => button.html).join(''),
			body: `
<div class="abDisplayMessage abDisplayMessage--column abDisplayMessage--withImageBg" aria-label="${errorMessage}" style="display: flex;">
	<img src="/static/v${js_params.version}/assets/images/global/noResultListsPlane.svg" class="abDisplayMessage--withImageBg-bgSvg"/>
	<div class="abDisplayMessage__msg"><span role="alert">${errorMessage}</span></div>
</div>
`,
			onOpen: () => {
				this.errorModal.html.addEventListener('click', (event) => {
					if (event.target.classList.contains('icon-circle-cross')) {
						this.errorModal.close();
					} else if (event?.target?.nodeName === 'BUTTON') {
						ctaButton.buttons.find(button => button.id === event.target.id).callback();
					}
				});
				this.errorModal.html.focus();
			},
			onClose: () => {
				this.errorModal.html.removeEventListener('click', ctaButton.callback);
			}
		});
		this.errorModal.open();
	}


	handleErrorMessage (error) {
		if (this.showresultMode) {
			EventDispatcher.dispatch('searchBox.searchError', error);
		}
		this.showErrorModal(error);
	}

	validateReturnDates () {
		if (!this.data.legs.length) {
			return;
		}

		const departDate = this.data.legs[0].departDate;


		if (this.data.tripType === 1) {
			const returnField = this.html.getElementsByClassName('abSearchBox__return')[0];
			let returnDate = this.data.legs[0].returnDate ?? returnField.dataset.date;
			const returnIsBeforeDeparture = dateIsBefore(new Date(returnDate), new Date(departDate));
			if (!returnDate || returnIsBeforeDeparture) {
				returnDate = add14Days(departDate);
			}

			this.setDateField(returnField, returnDate);
		}
	}

	validateMultiCityDates () {
		let valid = true;

		const legs = this.data.legs.map(leg => new Date(leg.departDate));

		for (let i = 1; i < legs.length; i++) {
			const leg = legs[i];
			const prevDate = legs[i - 1];
			if (!dateIsAfter(leg, prevDate) && !dateIsSameDay(leg, prevDate)) {
				valid = false;
				break;
			}
		}

		return valid;
	}

	setCalendarRendererView () {
		const wrapper = this.html.getElementsByClassName('abSearchBox__row--top')[0];
		const calendarRender = this.calendarRender.html;
		const styles = [
			'abCalendarWrapper--oneway',
			'abCalendarWrapper--return',
			'abCalendarWrapper--multi'
		];

		if (!wrapper.contains(calendarRender)) {
			wrapper.appendChild(this.calendarRender.html);
		}

		for (let i = 0; i < styles.length; i++) {
			calendarRender.classList.remove(styles[i]);
		}

		calendarRender.classList.add(styles[this.data.tripType]);
	}

	refreshCalendar (field) {
		const leg = field.closest('.abSearchBox__leg');
		const params = {
			elems: {},
			dates: {},
			tripType: this.data.tripType
		};

		params.elems.departField = leg.getElementsByClassName('abSearchBox__depart')[0];

		// If data('date') is falsy (likely a multi-city generated date field) then set depart date to today.
		params.dates.departDate = params.elems.departField.dataset.date || dateFormatYMD(new Date());

		if (params.tripType === 1) {
			params.elems.returnField = leg.getElementsByClassName('abSearchBox__return')[0];
			params.dates.returnDate = params.elems.returnField.dataset.date;
		}

		this.datePickers.render(params);

		let legNb = 0;

		// Calendar vertical positioning for multi view from tablet to bigger size
		if (params.tripType === 2) {
			legNb = parseInt(leg.getElementsByClassName('abSearchBox__circle--number')[0].innerHTML);
		}

		(legNb !== 0) ? this.calendarRender.show(legNb) : this.calendarRender.show();
	}

	setFixedStyles () {
		const quickFixDepart = this.html.querySelector('.abSearchBox__depart');
		const quickFixReturn = this.html.querySelector('.abSearchBox__return');
		const quickFixApply = this.html.querySelector('.abSearchBox__date-apply');
		const quickFix = window.innerHeight - 60 + 'px';

		quickFixDepart.style.top = quickFix;
		quickFixReturn.style.top = quickFix;
		quickFixApply.style.top = quickFix;
	}

	removeFixedStyles () {
		const quickFixDepart = this.html.querySelector('.abSearchBox__depart');
		const quickFixReturn = this.html.querySelector('.abSearchBox__return');
		const quickFixApply = this.html.querySelector('.abSearchBox__date-apply');

		quickFixDepart.style.top = '';
		quickFixReturn.style.top = '';
		quickFixApply.style.top = '';
	}

	enableShowresultMode () {
		this.showresultMode = true;

		const nav = this.html.getElementsByClassName('abSearchBox__nav')[0];

		nav.classList.add('abTabNav--transparent');
	}

	updateDataDates (departDate, returnDate) {
		if (this.data.legs.length !== 1) {
			return;
		}

		if (departDate) {
			this.data.legs[0].departDate = departDate;
		}

		if (returnDate) {
			this.data.legs[0].returnDate = returnDate;
		}

		return this;
	}


	focusPassengersField (elem = null) {
		if (window.innerWidth >= 992) {
			this.blurFields();
			this.calendarRender.blurField();
		}

		if (!elem) {
			elem = this.html.querySelector('.abSearchBox__passengers');
		}

		const focused = this.focusField(elem, 'abSearchBox__row--focus-passengers');

		if (isMobileView() && focused) {
			this.scrollTopView(elem, 40);
		}
	}

	sortPassengersAges () {
		const passengers = this.data.passengers;
		const agesToSort = ('youth' in passengers) ? passengers.youth.ages : passengers.child.ages;
		agesToSort.sort((a, b) => a - b);
	}

	focusTicketTypeCabin (elem) {
		if (window.innerWidth >= 992) {
			this.blurFields();
			this.calendarRender.blurField();
		}

		const cabin = this.html.getElementsByClassName('abSearchBox__cabin')[0];
		const focused = this.focusField(elem, 'abSearchBox__row--focus-cabin');

		if (isMobileView() && focused) {
			this.scrollTopView(cabin, 40);
		}
	}

	focusTripTypeView (elem) {
		if (window.innerWidth >= 992) {
			this.blurFields();
			this.calendarRender.blurField();
		}

		this.focusField(elem, 'abSearchBox__row--focus-view');
	}

	setCheckboxes () {
		this.setDirectCheckbox();
		this.setBaggageCheckbox();
		this.setCabinBaggageCheckbox();
		this.setClimateCompCheckbox();
		this.setFlexTicketCheckbox();
		this.setNearbyCheckbox();
	}

	resetSearchBox () {
		// this.data = deepCopy(this.initialData);
		this.initData(deepCopy(this.initialData));

		this.setView();
		this.setCheckboxes();
		this.setCabin();

		if (this.data.legs.length) {
			const legs = this.html.getElementsByClassName('abSearchBox__leg');
			this.data.legs.forEach((legData, index) => {
				const leg = legs[index];
				if (leg) {
					let field = leg.getElementsByClassName('abSearchBox__depart')[0];
					let date = legData.departDate || _defaults.dates.from;

					this.setupDateField({ field, date });

					if (this.data.tripType === 1) {
						field = leg.getElementsByClassName('abSearchBox__return')[0];
						date = legData.returnDate || _defaults.dates.to;

						this.setupDateField({ field, date });
					}
				} else {
					this.renderMultiCityLeg(index);
				}
			});

			if (this.data.tripType === 2) {
				const numberOfLegs = this.data.legs.length;
				this.renderMultiCitySummary();

				while (this.searchFields.length > numberOfLegs) {
					this.removeLeg(this.searchFields.length - 1);
				}

				const btn = this.html.getElementsByClassName('abSearchBox__add-leg')[0];

				classList(btn).toggle('disabled', numberOfLegs >= this.maxNumberOfLegs);

				this.renderMultiCityRemoveBtns();
			}
		}
	}

	events () {
		document.addEventListener('click', async (event) => {
			const elem = event.target;


			if (elem.matches('.abSearchBox__passengers')) {
				event.stopPropagation();
				this.focusPassengersField();

				return;
			}

			if (elem.matches('button')) {
				const action = elem.dataset.action;

				if (action === 'set-view') {
					const newType = parseInt(elem.dataset.type, 10);

					if (window.innerWidth >= 992) {
						this.blurFields();
						this.calendarRender.blurField();
					}

					this.data.tripType = newType;

					if (newType === 1) /* return */ {
						this.validateReturnDates();
					}

					// Only log if it is a human event and not on arrival setup
					if (qs('.abSearchBox--extended .abSearchBox__view .abBtn--ticked')) {
						logEvent(events.editsearchTripType, eventCategories.search, this.data.tripType);
					}

					this.setView();
					this.updateLegs(true);
				} else if (action === 'switcher') {
					const leg = elem.closest('.abSearchBox__leg');
					const legIndex = parseInt(leg.querySelector('.abSearchBox__circle--number').innerText) - 1 || 0;
					const fromCheckedIatas = this.data.legs[legIndex].fromIatas || [];
					const toCheckedIatas = this.data.legs[legIndex].toIatas || [];


					// Update this.data.legs
					this.data.legs[legIndex].fromIatas = toCheckedIatas;
					this.data.legs[legIndex].toIatas = fromCheckedIatas;

					elem.classList.toggle('abSearchBox__switcher--rotate');

					logEvent(events.editsearchSwitchDestination, eventCategories.search);
				} else if (action === 'search') {
					this.blurFields();
					this.submit();
					return;
				} else if (action === 'add-leg') {
					const numOfLegs = this.data.legs.length;
					const btn = elem.closest('.abSearchBox__add-leg');

					if (numOfLegs < this.maxNumberOfLegs) {
						this.renderMultiCityLeg();

						elem.disabled = (numOfLegs === (this.maxNumberOfLegs - 1)) ? true : false;
						if (numOfLegs === (this.maxNumberOfLegs - 1)) {
							btn.classList.add('disabled');
						}
					} else {
						elem.disabled = false;
					}

					this.renderMultiCityRemoveBtns();
					this.updateLegs();
					EventDispatcher.dispatch('searchBox:legAdded');
				} else if (action === 'remove-leg') {
					const leg = elem.closest('.abSearchBox__leg');
					const legIndex = parseInt(leg.dataset.legIndex, 10);
					qs('.abSearchBox__add-leg', this.html).disabled = false;

					await slideUp(leg);

					this.removeLeg(legIndex);

					this.renderMultiCityLegNumbers();
					this.renderMultiCityRemoveBtns();
					EventDispatcher.dispatch('searchBox:legRemoved', this.data.legs.length);

					if (this.showresultMode) {
						this.renderMultiCitySummary();
					}
				} else if (action === 'apply-date') {
					this.blurFields();
					this.calendarRender.hide();
				} else if (action === 'set-cabin') {
					event.stopPropagation();

					const cabin = this.html.getElementsByClassName('abSearchBox__cabin')[0];
					const btns = cabin.getElementsByTagName('button');
					const input = cabin.getElementsByClassName('abSearchBox__value')[0];
					const value = elem.value;

					// Only log if it is a human event and not on arrival setup
					// It needs != because the value can be 0 and 0 !== 0 is true, because one is string and the other is number
					if (qs('.abSearchBox--extended .abSearchBox__cabin .abBtn--ticked') && this.data.cabin != value) {
						logEvent(events.editsearchTicketType, eventCategories.search, this.data.cabin);
					}

					this.data.cabin = parseInt(value, 10);
					this.blurFields();

					// Only log if it is a human event and not on arrival setup
					if (qs('.abSearchBox--extended .abSearchBox__cabin .abBtn--ticked')) {
						// This is adding in unfocus...
						logEvent(events.editsearchTicketType, eventCategories.search, this.data.cabin);
					}

					for (let i = 0; i < btns.length; i++) {
						btns[i].classList.remove('abBtn--ticked');
					}

					elem.classList.add('abBtn--ticked');
					input.innerHTML = elem.textContent;
				}
			}

			if (elem.matches('.abSearchBox__depart') || elem.closest('.abSearchBox__duration') || elem.closest('.abSearchBox__return')) {
				this.focusCalendar(elem);
				return;
			}

			if (!elem.matches('.SearchFieldMulti__field--from') && elem.closest('.SearchFieldMulti--from')) {
				if (window.innerWidth >= 992) {
					this.blurFields();
					this.calendarRender.blurField();
				}

				return;
			}

			if (!elem.matches('.SearchFieldMulti__field--to') && elem.closest('.SearchFieldMulti--to')) {
				if (window.innerWidth >= 992) {
					this.blurFields();
					this.calendarRender.blurField();
				}

				return;
			}

			if (elem.matches('.abSearchBox__overlay')) {
				event.stopPropagation();

				this.calendarRender.blurField();
				this.calendarRender.hide();

				this.blurFields();

				return;
			}


			if (elem.matches('.abSearchBox__cabin')) {
				event.stopPropagation();
				this.focusTicketTypeCabin(elem);
				return;
			}

			if (elem.matches('.abSearchBox__view')) {
				event.stopPropagation();
				this.focusTripTypeView(elem);
				return;
			}

			if (window.innerWidth >= 992) {
				if (elem.closest('.abSearchBox__menu') || elem.closest('.toast-overlay')) {
					return;
				}

				if (
					elem.closest('.abSearchBox__checkboxes')
					|| !elem.closest('.abSearchBox')
					|| !elem.closest('.abCalendarWrapper')
				) {
					this.blurFields();
					this.calendarRender.blurField();
				}
			}
		}, false);

		document.addEventListener('keyup', (event) => {
			const elem = event.target;

			if (event.key === 'Enter' || event.key === 'Space') {
				if (elem.matches('.abSearchBox__depart') || elem.closest('.abSearchBox__return')) {
					event.stopPropagation();
					this.focusCalendar(elem);
					return;
				}

				if (elem.matches('.abSearchBox__passengers')) {
					event.stopPropagation();
					this.focusPassengersField(elem);
					return;
				}

				if (elem.matches('.abSearchBox__cabin')) {
					event.stopPropagation();
					this.focusTicketTypeCabin(elem);
					return;
				}

				if (elem.matches('.abSearchBox__view')) {
					event.stopPropagation();
					this.focusTripTypeView(elem);
				}
			}
		}, false);

		EventDispatcher.addEventListener('submitSearch', () => this.submit());

		// EventDispatcher.addEventListener('showSearchBoxYouthPassengers', () => {
		// 	if (this.isYouthTicketEnabled) {
		// 		this.focusPassengersField();
		// 	}
		// });

		EventDispatcher.addEventListener('searchBoxReset', () => this.resetSearchBox());

		document.addEventListener('change', (event) => {
			const elem = event.target;

			if (elem.matches('.abSearchBox__nearby')) {
				this.data.nearbyDates = +elem.checked;

				if (this.data.nearbyDates === 1) {
					logEvent(events.editsearchFlexibleDatesEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchFlexibleDatesDisabled, eventCategories.search);
				}
				return;
			}

			if (elem.matches('.abSearchBox__baggage')) {
				this.data.baggage = +elem.checked;

				if (this.data.baggage === 1) {
					logEvent(events.editsearchBaggageEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchBaggageDisabled, eventCategories.search);
				}

				return;
			}

			if (elem.matches('.abSearchBox__cabinBaggage')) {
				this.data.cabinBaggage = +elem.checked;

				if (this.data.cabinBaggage === 1) {
					logEvent(events.editsearchCabinBaggageEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchCabinBaggageDisabled, eventCategories.search);
				}
				return;
			}

			if (elem.matches('.checkbox-direct')) {
				this.data.direct = +elem.checked;

				if (this.data.direct === 1) {
					logEvent(events.editsearchDirectFlightOnlyEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchDirectFlightOnlyDisabled, eventCategories.search);
				}

				return;
			}

			if (elem.matches('.abSearchBox__cabinBaggage')) {
				this.data.cabinBaggage = +elem.checked;

				if (this.data.cabinBaggage === 1) {
					logEvent(events.editsearchCabinBaggageEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchCabinBaggageDisabled, eventCategories.search);
				}

				return;
			}

			if (elem.matches('.abSearchBox__climateComp')) {
				this.data.climateComp = +elem.checked;

				if (this.data.climateComp === 1) {
					logEvent(events.editsearchCO2CompensateEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchCO2CompensateDisabled, eventCategories.search);
				}

				return;
			}

			if (elem.matches('.abSearchBox__flexTicket')) {
				this.data.flexTicket = +elem.checked;

				if (this.data.flexTicket === 1) {
					logEvent(events.editsearchRebookableEnabled, eventCategories.search);
				} else {
					logEvent(events.editsearchRebookableDisabled, eventCategories.search);
				}
			}
		}, false);

		window.addEventListener('resize', (event) => {
			event.stopPropagation();

			if (this.showresultMode && this.data.tripType === 2) {
				this.renderMultiCitySummary();
			}
		}, false);

		// calendarRender events

		// TODO: temp solution to fixed elements on showresult page...
		if (this.showresultMode) {
			let resizeTimer;

			window.addEventListener('resize', (event) => {
				event.stopPropagation();

				if (resizeTimer) {
					cancelAnimationFrame(resizeTimer);
				}

				resizeTimer = requestAnimationFrame(() => {
					if (isMobileView()) {
						if (this.calendarRender.open) {
							this.setFixedStyles();
						} else {
							this.removeFixedStyles();
						}
					} else {
						this.removeFixedStyles();
					}
				});
			});
		}

		this.html.addEventListener('blurFields', event => this.blurFields());

		// Show and hide searchbox overlay depending on the opened / closed state of the searchFielsMulti respective modal
		EventDispatcher.addEventListener('searchFieldMulti.modalOpened', (isOpened) => {
			const overlay = document.getElementById('abSearchBox');
			if (isOpened) {
				overlay.classList.add('abSearchBox--show-overlay');
			} else {
				overlay.classList.remove('abSearchBox--show-overlay');
			}
		});
	}

	removeLeg (legIndex) {
		if (!this.data.legs[legIndex]) {
			return;
		}
		this.searchFields[legIndex].from.component.unmount();
		this.searchFields[legIndex].to.component.unmount();
		this.data.legs.splice(legIndex, 1);
		this.searchFields.splice(legIndex, 1);

		// Update legIndex in remaining props
		this.searchFields.forEach((leg, index) => {
			if (leg.from && leg.from.props) {
				leg.from.props.legIndex = index;
			}
			if (leg.to && leg.to.props) {
				leg.to.props.legIndex = index;
			}
		});

		const leg = this.html.querySelector(`.abSearchBox__leg[data-leg-index="${legIndex}"]`);
		leg.remove();
	}
}


export function scrollCalendars (direction) {
	const disabled = 'abCalendarWrapper__slider--disabled';
	const buttons = qsa('.abCalendarWrapper__slider');
	const width = qs('.Calendar').offsetWidth;
	const inner = qs('.Calendars__inner');
	const scrolled = inner.scrollLeft;
	const maxScroll = inner.scrollWidth - inner.offsetWidth;

	if (typeof direction !== 'undefined') {
		const scrollLeft = (direction === 'right') ? (scrolled + width) : (scrolled - width);

		// Round up or down to the nearest multiple of width
		const rounded = Math.round(scrollLeft / width) * width;

		if (scrollLeft > 0) {
			buttons[0].classList.remove(disabled);
		} else {
			buttons[0].classList.add(disabled);
		}

		if (scrollLeft >= maxScroll) {
			buttons[1].classList.add(disabled);
		} else {
			buttons[1].classList.remove(disabled);
		}

		// inner.scrollTo({ left: scrollLeft, behavior: 'smooth' });
		inner.scrollTo({ left: rounded, behavior: 'smooth' });
	} else {
		if (scrolled > 0) {
			buttons[0].classList.remove(disabled);
		} else {
			buttons[0].classList.add(disabled);
		}

		if (scrolled >= maxScroll) {
			buttons[1].classList.add(disabled);
		} else {
			buttons[1].classList.remove(disabled);
		}
	}
}
